// ─────────────────────────────────────────────────────────────────
// Glass-i — Payment / Receipt Voucher API client (Stage 6B.6).
//
// Thin axios wrappers around /api/erp/accounts/payments/* and the
// 3 lookup endpoints (customers / suppliers / cash-bank ledgers).
// Approval-MFA challenge id flows through the standard header on
// post / cancel.
// ─────────────────────────────────────────────────────────────────

import client from '../../../../api/client';

const BASE = '/api/erp/accounts';
const PAY  = `${BASE}/payments`;

function unwrap(resp) {
  return resp && resp.data && resp.data.data !== undefined
    ? resp.data.data
    : resp && resp.data;
}

// camelCase grid field → snake_case backend whitelist.
const GRID_FIELD_TO_SQL = {
  paymentNo:    'payment_no',
  paymentType:  'payment_type',
  paymentDate:  'payment_date',
  partyType:    'party_type',
  mode:         'mode',
  amount:       'amount',
  status:       'status',
  postedAt:     'posted_at',
  createdAt:    'created_at',
  // Stage 6B.7C — cheque tracking fields.
  chequeNo:     'cheque_no',
  chequeStatus: 'cheque_status',
  chequeDate:   'cheque_date'
};
function mapField(c) {
  return c && typeof c === 'string' ? (GRID_FIELD_TO_SQL[c] || c) : c;
}
function mapRules(rules) {
  return rules.map((r) => {
    if (!r || typeof r !== 'object') return r;
    if (Array.isArray(r.rules)) return { ...r, rules: mapRules(r.rules) };
    if (typeof r.field === 'string' && r.field) return { ...r, field: mapField(r.field) };
    return r;
  });
}
function translateQuery(q) {
  if (!q || typeof q !== 'object') return q;
  const out = { ...q };
  if (Array.isArray(out.filters)) {
    out.filters = out.filters.filter((f) => f && f.field)
      .map((f) => ({ ...f, field: mapField(f.field) }));
  }
  if (Array.isArray(out.sort)) {
    out.sort = out.sort.filter((s) => s && s.field)
      .map((s) => ({ ...s, field: mapField(s.field) }));
  }
  if (typeof out.groupBy === 'string' && out.groupBy) out.groupBy = mapField(out.groupBy);
  if (out.advancedFilters && Array.isArray(out.advancedFilters.rules)) {
    out.advancedFilters = {
      ...out.advancedFilters,
      rules: mapRules(out.advancedFilters.rules)
    };
  }
  return out;
}

export const paymentVoucherApi = {
  list: (query) =>
    client.post(`${PAY}/list`, translateQuery(query) || {}).then(unwrap),
  get:  (id) => client.get(`${PAY}/${encodeURIComponent(id)}`).then(unwrap),
  createDraft: (payload) => client.post(PAY, payload).then(unwrap),
  updateDraft: (id, payload) =>
    client.put(`${PAY}/${encodeURIComponent(id)}`, payload).then(unwrap),
  post: (id, { approvalMfaChallengeId } = {}) =>
    client.post(`${PAY}/${encodeURIComponent(id)}/post`, {}, {
      headers: approvalMfaChallengeId
        ? { 'x-approval-mfa-challenge-id': approvalMfaChallengeId }
        : undefined
    }).then(unwrap),
  cancel: (id, body, { approvalMfaChallengeId } = {}) =>
    client.post(`${PAY}/${encodeURIComponent(id)}/cancel`, body || {}, {
      headers: approvalMfaChallengeId
        ? { 'x-approval-mfa-challenge-id': approvalMfaChallengeId }
        : undefined
    }).then(unwrap),
  // Stage 6B.9F — draft delete now carries the Approval-MFA challenge.
  remove: (id, { approvalMfaChallengeId } = {}) =>
    client.delete(`${PAY}/${encodeURIComponent(id)}`, {
      headers: approvalMfaChallengeId
        ? { 'x-approval-mfa-challenge-id': approvalMfaChallengeId }
        : undefined
    }).then(unwrap),

  // Lookups
  customerLookup: (q, limit = 50) =>
    client.get(`${BASE}/parties/customers`, {
      params: { ...(q ? { q } : {}), limit }
    }).then(unwrap),
  supplierLookup: (q, limit = 50) =>
    client.get(`${BASE}/parties/suppliers`, {
      params: { ...(q ? { q } : {}), limit }
    }).then(unwrap),
  cashBankLedgerLookup: (q, limit = 50) =>
    client.get(`${BASE}/ledgers/cash-bank`, {
      params: { ...(q ? { q } : {}), limit }
    }).then(unwrap)
};

export default paymentVoucherApi;
